import { useContext, useEffect } from "react";

import { userMediaContext } from "../contexts/userMediaContext";

/*
  useUserMedia: Use it when you just want to read the state of the stream(s) stored in the UserMediaContextProvider without auto start/stop of any stream.
    - This is the single source of truth to interact with the user media stream(s) tracks across the app (see UserMediaContextProvider for details)
    - Currently only used for preview stream, used by device selection / call setting components
    - For the main publishing stream use MEETING_STATE.webcamStream & MEETING_STATE.micStream from the global context.
      These are kept in sync with janus's tracks in videoroomtest.js (on videoroom plugin event handlers, defined when attaching the plugin)

  useUserMediaPreviewStreamConsumer: Use it this when you require a preview stream to be present (auto start/stop the preview stream & tracks)
    - It maintains a single user media stream for preview (with active tracks) for the entire app.
    - It starts/stops the relevant stream automatically by tracking the consumers.
      - It creates a new stream when the first consumer is mounted, or uses the existing one for next consumers.
      - It stops the stream when all consumers are unmounted.
*/

export function useUserMedia() {
  const context = useContext(userMediaContext);
  if (!context) throw new Error("useUserMedia must be used within a UserMediaProvider");
  return context;
}

export function useUserMediaPreviewStreamConsumer(shouldRequestPermissions = false) {
  const context = useUserMedia();
  useEffect(() => {
    if (shouldRequestPermissions) {
      context.setPreviewStreamConsumers((prev) => prev + 1);
      return () => {
        context.setPreviewStreamConsumers((prev) => prev - 1);
      };
    }
  });
  return context;
}
